/**
 * V2 Customer Settings page — companion to `SiteSettingsViewV2` and
 * `SiteGroupSettingsViewV2`.
 *
 * Reads/writes the consolidated `/api/odl-app/customer-settings/{id}/`
 * endpoint via the `features/customerSettings/` hooks.  Section
 * presence is the visibility signal — the backend includes a section
 * iff the requester can see it; absence here means hide the section.
 *
 * Most sections are presence-only mounts of the legacy tab
 * components (Contacts, Agreements, Pricing, Attachments,
 * Authentication) — feature parity with V1 without rebuilding their
 * internals.  General + Admin (options, features, integrations) +
 * addresses write through the unified Save bar via the consolidated
 * PATCH (one round-trip per save).  Third Party + Reseller Management
 * also fold into the unified Save bar, but through their own side
 * dispatches (bulk-edit / client-reseller PUT endpoints, not the
 * sectioned PATCH): each signals dirtiness via a synthetic Formik
 * field and exposes an imperative submit() the Save flow fires when
 * the section is dirty and the user holds its write permission.
 * Special Instructions still fire immediate per-edit CRUD against the
 * legacy CustomerSpecialInstruction endpoints — that matches V1 UX
 * (the dialog's Save button commits) and there's no synthetic-field
 * fan-out to fold in.
 *
 * Each section's own fields/handlers live under
 * `sections/customer/`; this file is the shell — routing, formik-
 * value normalization, and save orchestration.
 */

import { Stack } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import * as yup from 'yup';

import { useUser } from '@sophocles/app';
import LoadingScreen from '@sophocles/components/LoadingScreen';
import { getSelectedCustomerSelector } from '@sophocles/features/auth/authSelectors';
import { loadBillingAccountAgreements } from '@sophocles/features/billing/api';
import { useCustomerSettings } from '@sophocles/features/customerSettings';
import { selectCustomerFeatureAssociationByKey } from '@sophocles/features/featureFlags/slice';
import { selectDisableThirdPartyApi, selectSeparateTenant } from '@sophocles/features/tenant/slice';
import useBreadcrumbs from '@sophocles/hooks/useBreadcrumbs';
import useCustomer from '@sophocles/hooks/useCustomer';
import CustomerDeactivatePanel from '@sophocles/views/customers/components/CustomerDeactivatePanel';
import { SettingsForm, SettingsPageShell } from '@sophocles/views/settings/components';

import AdminSection from './sections/customer/AdminSection';
import AgreementsSection from './sections/customer/AgreementsSection';
import AttachmentsSection from './sections/customer/AttachmentsSection';
import AuthenticationSection from './sections/customer/AuthenticationSection';
import ContactsSection from './sections/customer/ContactsSection';
import GeneralSection from './sections/customer/GeneralSection';
import PricingSection from './sections/customer/PricingSection';
import ResellerManagementSection from './sections/customer/ResellerManagementSection';
import ThirdPartySection from './sections/customer/ThirdPartySection';

// Project a server-side address payload to the writable subset
// the consolidated PATCH expects.  Both initialValues seeding and
// the `BillingShippingAddresses` onChange path go through this
// projection so dirty-tracking compares like-for-like.
const pickAddressFields = addr => ({
  address_1: addr?.address_1 ?? '',
  address_2: addr?.address_2 ?? '',
  city: addr?.city ?? '',
  state: addr?.state ?? '',
  zipcode: addr?.zipcode ?? '',
  country: addr?.country ?? '',
});

// Yup schema mirrors Customer model + V1 form constraints.  ``name``
// max 50 + required; ``description`` max 100 + required (V1 form
// required it even though the model allows blank — preserve the
// stricter UX rule); ``website`` max 200, optional.
const validationSchema = yup.object({
  general: yup
    .object({
      name: yup.string().trim().required('Name is required.').max(50, 'Name must be 50 characters or fewer.'),
      // Optional, because the column is blank on a good share of the
      // customers that exist and the endpoint accepts it that way. Demanding
      // it here made the whole form invalid, and the Save bar reads one
      // form-wide validity, so a customer with no description could not save
      // any section — including the two whose writes go to their own
      // endpoints and never look at this field.
      description: yup.string().trim().max(100, 'Description must be 100 characters or fewer.'),
      website: yup.string().trim().max(200, 'Website must be 200 characters or fewer.'),
    })
    .nullable(),
  admin: yup
    .object({
      options: yup.object().nullable(),
    })
    .nullable(),
});

function CustomerSettingsViewV2(props) {
  const { t } = useTranslation();
  const params = useParams();
  const dispatch = useDispatch();
  const customerId = Number(params.cust_id);
  // useCustomer seeds the Redux customer context (setCustomer dispatch,
  // fetchCustomer with 5-min cache, loadCustomerFeatureAssociations,
  // loadCustomerTypes, loadFeatures). The Immutable.Map variant
  // (getSelectedCustomerSelector) is used below because every legacy
  // presence-only tab component calls customer.get('id', 0) and friends.
  useCustomer(customerId);
  const customer = useSelector(getSelectedCustomerSelector);
  const { settings, status, save, reload } = useCustomerSettings({ customerId });
  const { user, isSuperUser, isStaff, permissions } = useUser();
  const autoBreadcrumbs = useBreadcrumbs();
  const canEdit = permissions.canEditCustomer;
  const canEditOptions = permissions.canEditCustomerOptions;

  // Imperative handles for the two sections whose writes hit separate
  // endpoints (Third Party bulk-edit, Reseller client/reseller PUT);
  // handleSave calls their submit() when their synthetic dirty flag
  // is set and the write permission holds.
  const thirdPartyRef = React.useRef(null);
  const resellerRef = React.useRef(null);

  const separateTenant = useSelector(selectSeparateTenant);

  const accountId = customer?.get?.('billing_account_id', null) ?? null;
  const apiFeature = useSelector(selectCustomerFeatureAssociationByKey(customerId, 'API'));
  const disableThirdPartyApi = useSelector(selectDisableThirdPartyApi);

  const hasApiAccess = Boolean(apiFeature?.is_enabled) && !disableThirdPartyApi;
  const canEditAgreements = permissions.canEditBillingAgreements;
  const canEditAttachments = permissions.canEditCustomerAttachments;

  // Section presence is the visibility signal — backend returns a
  // section iff the requester can see it.
  const hasAdmin = Boolean(settings?.admin) && !separateTenant;
  const hasInstructions = Boolean(settings?.instructions) && !separateTenant;
  const hasContacts = Boolean(settings?.contacts) && !(__EXCLUDE_SALES__ && __EXCLUDE_RETURNS__);
  const hasAgreements = Boolean(settings?.agreements) && !__EXCLUDE_BILLING__;
  const hasPricing = Boolean(settings?.pricing) && !__EXCLUDE_BILLING__;
  const hasAttachments = Boolean(settings?.attachments) && !separateTenant;
  const hasAuthentication = Boolean(settings?.authentication) && !separateTenant;
  const hasThirdParty = Boolean(settings?.third_party);
  const hasResellerManagement = Boolean(settings?.reseller_management) && !separateTenant;

  const hasAdminFeatures = Boolean(settings?.admin?.features) && !separateTenant;
  const hasAdminIntegrations = Boolean(settings?.admin?.integrations) && !disableThirdPartyApi;

  // Write capability for the two side-dispatch sections.  Reseller
  // needs both assign + unassign perms; Third Party has no distinct FE
  // perm — section presence (staff-gated by the backend) is the write
  // gate, matching V1 where its Save button gated on dirty alone.
  const canManageResellers =
    hasResellerManagement && permissions.canAssignResellerCustomers && permissions.canUnassignResellerCustomers;
  const canManageThirdParty = hasThirdParty;

  // Load agreements upfront so the Pricing section can read the
  // active one synchronously.  Mirrors V1's componentDidMount.
  React.useEffect(() => {
    if (hasAgreements && accountId != null) {
      dispatch(loadBillingAccountAgreements({ account: accountId }));
    }
  }, [dispatch, hasAgreements, accountId]);

  // Sidebar sections — driven by section presence on the response.
  // Memoized so SettingsPageShell's registration effect doesn't
  // re-fire on every render.
  const sections = React.useMemo(() => {
    const list = [{ id: 'settings-general', title: t('customerSettings.v2.general', 'General'), icon: 'info' }];
    if (hasAdmin) {
      list.push({
        id: 'settings-admin',
        title: t('customerSettings.v2.admin', 'Admin'),
        icon: 'adminPanelSettings',
        pillText: t('siteSettings.adminOnly', 'Admin Only'),
      });
    }
    if (hasContacts) {
      list.push({
        id: 'settings-contacts',
        title: t('customerSettings.v2.contacts', 'Contacts'),
        icon: 'contactPhone',
        pillText: t('siteSettings.staffOnly', 'Staff Only'),
      });
    }
    if (hasAgreements) {
      list.push({
        id: 'settings-agreements',
        title: t('customerSettings.v2.agreements', 'Agreements'),
        icon: 'receiptLong',
        pillText: t('siteSettings.accountingOnly', 'Accounting Only'),
      });
    }
    if (hasPricing) {
      list.push({
        id: 'settings-pricing',
        title: t('customerSettings.v2.pricing', 'Pricing'),
        icon: 'paid',
        pillText: t('siteSettings.accountingOnly', 'Accounting Only'),
      });
    }
    if (hasAttachments) {
      list.push({
        id: 'settings-attachments',
        title: t('customerSettings.v2.attachments', 'Attachments'),
        icon: 'attachFile',
        pillText: t('siteSettings.staffOnly', 'Staff Only'),
      });
    }
    if (hasAuthentication) {
      list.push({
        id: 'settings-authentication',
        title: t('userSettings.authentication', 'Authentication'),
        icon: 'lock',
        pillText: t('siteSettings.adminOnly', 'Admin Only'),
      });
    }
    if (hasThirdParty) {
      list.push({
        id: 'settings-third-party',
        title: t('customer.3rdParty', 'Third Party'),
        icon: 'hub',
        pillText: t('siteSettings.staffOnly', 'Staff Only'),
      });
    }
    if (hasResellerManagement) {
      list.push({
        id: 'settings-reseller-management',
        title: t('customer.resellerManagement', 'Reseller Management'),
        icon: 'business',
        pillText: t('siteSettings.staffOnly', 'Staff Only'),
      });
    }
    return list;
  }, [
    t,
    hasAdmin,
    hasContacts,
    hasAgreements,
    hasPricing,
    hasAttachments,
    hasAuthentication,
    hasThirdParty,
    hasResellerManagement,
  ]);

  if (!settings || status === 'loading') {
    return <LoadingScreen />;
  }

  const initialValues = {
    general: {
      name: settings.general?.name || '',
      description: settings.general?.description || '',
      ...(!separateTenant ? { website: settings.general?.website || '' } : {}),
      // Staff-only fields — only present in the response when the
      // requester is staff.  Spread conditionally so non-staff
      // initial values don't carry stray keys that would dirty the
      // diff on a no-op save.
      ...(!separateTenant && settings.general?.customer_type !== undefined
        ? { customer_type: settings.general.customer_type }
        : {}),
      ...(!separateTenant && settings.general?.is_billboard !== undefined
        ? { is_billboard: settings.general.is_billboard }
        : {}),
    },
    ...(hasAdmin
      ? {
          admin: {
            options: {
              requires_po_number_for_orders: Boolean(settings.admin?.options?.requires_po_number_for_orders),
              requires_auth_name_for_orders: Boolean(settings.admin?.options?.requires_auth_name_for_orders),
              sells_digital_panels: Boolean(settings.admin?.options?.sells_digital_panels),
              webhook_enabled: Boolean(settings.admin?.options?.webhook_enabled),
              can_use_bulk_commands: Boolean(settings.admin?.options?.can_use_bulk_commands),
              uses_relay_monitor_by_default: Boolean(settings.admin?.options?.uses_relay_monitor_by_default),
            },
          },
        }
      : {}),
    // Mirror the server's address shape so dirty-tracking compares
    // against the actual loaded values instead of dirtying the form
    // on first paint.  Omitted when addresses are excluded at build
    // time (both SALES and RETURNS excluded) so the Formik diff
    // stays clean and the PATCH never sends an address payload.
    ...(!(__EXCLUDE_SALES__ && __EXCLUDE_RETURNS__)
      ? {
          addresses: {
            billing: pickAddressFields(settings.addresses?.billing),
            shipping: pickAddressFields(settings.addresses?.shipping),
          },
        }
      : {}),
    // Synthetic Formik fields — Third Party / Reseller sections flip
    // these to true when their selection diverges from what loaded so
    // the unified Save bar activates.  Stripped before the settings
    // PATCH; the sections' imperative submit() fires in handleSave.
    ...(hasThirdParty ? { __pending_third_party_dirty: false } : {}),
    ...(hasResellerManagement ? { __pending_reseller_dirty: false } : {}),
  };

  // Save flow: one PATCH against the consolidated endpoint for the
  // sectioned fields, plus side dispatches for the two sections whose
  // writes hit separate endpoints.  Addresses ride along in the
  // section diff via Formik's ``addresses`` field (see GeneralSection's
  // AddressesSubsection).  Each side dispatch fires only when its
  // synthetic dirty flag is set and the section's write permission
  // holds; failures propagate to SettingsForm's error snackbar.
  const handleSave = async diff => {
    const { __pending_third_party_dirty, __pending_reseller_dirty, ...settingsDiff } = diff;
    if (Object.keys(settingsDiff).length > 0) {
      await save(settingsDiff);
    }
    if (__pending_third_party_dirty && canManageThirdParty && thirdPartyRef.current?.submit) {
      await thirdPartyRef.current.submit();
    }
    if (__pending_reseller_dirty && canManageResellers && resellerRef.current?.submit) {
      await resellerRef.current.submit();
    }
  };

  const settingsLabel = t('customerSettings.v2.title', 'Settings');
  const breadcrumbs = (() => {
    const autoCrumbs = autoBreadcrumbs.slice();
    return [...autoCrumbs, { label: settingsLabel }];
  })();

  // "View-only" banner — fires when the user can't edit any of the
  // visible writable surfaces.  Admin needs change_customeroptions;
  // General needs change_customer; Reseller / Third Party each add
  // their own writable surface, so a user who can manage either is not
  // view-only.  Doubles as the SettingsForm readOnly gate so the Save
  // bar renders whenever any surface is editable.
  const isFullyViewOnly = !canEdit && !canEditOptions && !canManageResellers && !canManageThirdParty;

  return (
    <SettingsPageShell
      page="customer_settings"
      breadcrumbs={breadcrumbs}
      contextName="customer"
      basePath={`/c/${customerId}`}
      sections={sections}
      sectionParam={params.section}
      viewOnly={isFullyViewOnly}
      viewOnlyMessage={t(
        'customerSettings.v2.viewOnly',
        "You can view this customer's settings but don't have permission to edit. Contact an administrator to request edit access.",
      )}
    >
      <SettingsForm
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSave={handleSave}
        readOnly={isFullyViewOnly}
      >
        <Stack spacing={3}>
          {/*
                Per-section disable wrappers — General requires
                change_customer; Admin requires change_customeroptions.
                A single umbrella `<fieldset disabled>` would block
                edits from users who hold one perm but not the other.
              */}
          <Stack component="fieldset" disabled={!canEdit} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
            <GeneralSection
              customerId={customerId}
              settings={settings}
              separateTenant={separateTenant}
              canEdit={canEdit}
              onLogoChanged={reload}
            />
          </Stack>

          <CustomerDeactivatePanel customerId={customerId} customerName={settings.general?.name} />

          {hasAdmin ? (
            <Stack component="fieldset" disabled={!canEditOptions} sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}>
              <AdminSection
                customerId={customerId}
                hasFeatures={hasAdminFeatures}
                hasIntegrations={hasAdminIntegrations}
              />
            </Stack>
          ) : null}

          {hasContacts ? <ContactsSection customerId={customerId} /> : null}
          {hasAgreements ? <AgreementsSection accountId={accountId} canEditAgreements={canEditAgreements} /> : null}
          {hasPricing ? <PricingSection customer={customer} accountId={accountId} /> : null}
          {hasAttachments ? (
            <AttachmentsSection {...props} customer={customer} user={user} canWrite={canEditAttachments} />
          ) : null}
          {hasAuthentication ? (
            <AuthenticationSection
              {...props}
              customer={customer}
              user={user}
              isSuperUser={isSuperUser}
              isStaff={isStaff}
            />
          ) : null}
          {hasThirdParty ? <ThirdPartySection ref={thirdPartyRef} customer={customer} /> : null}
          {hasResellerManagement ? (
            <ResellerManagementSection ref={resellerRef} {...props} customer={customer} />
          ) : null}
        </Stack>
      </SettingsForm>
    </SettingsPageShell>
  );
}

export default CustomerSettingsViewV2;
